import React, { useMemo } from 'react';
import { IUICfgComponentItemsContainer } from '@canvas-logic/clp-api-types';
import { ConfiguratorUIElementStates, viewElementId } from '@/elements/base/ElementState';
import { ContainerLevelContext } from '@/ui-kit/contexts/ContainerLevelContext';
import { gapLevelToCssValue } from '@/elements/shared/gapUtils';
import { ElementRenderer } from '@/elements/ElementRenderer';
import { DisplayContextProvider } from '@canvas-logic/clp-shared-ui';
import { useSlotDisplayContext } from '@/localization/useSlotDisplayContext';

interface ComponentItemsContainerCfgUIElementProps {
  viewElement: IUICfgComponentItemsContainer;
  childrenItems: ConfiguratorUIElementStates[];
}

/**
 * Component Items Container - groups a component with its attributes.
 *
 * This container is always visible (no expand/collapse) and has no container-level
 * title or description. The component inside displays its own name as the visual label.
 */
export const ComponentItemsContainerCfgUIElement: React.FC<ComponentItemsContainerCfgUIElementProps> = ({
  viewElement,
  childrenItems,
}) => {
  // Get level from viewElement (set by virtualLayoutBuilder)
  const level = (viewElement as any)._level ?? 1;

  // Structure: [Component, ...Attributes]
  const componentChild = useMemo(() => childrenItems[0], [childrenItems]);
  const attributeChildren = useMemo(() => childrenItems.slice(1), [childrenItems]);

  // Pull the slot's componentKey from the first child (typed as
  // [IUICfgComponent, ...] in IUICfgComponentItemsContainer). This drives the
  // shared DisplayContext so attribute siblings — not just products under the
  // component — participate in the `cpt.X.cmp.Y.*` cascade (ADR-0045 gap #2).
  const componentKey = (componentChild as { viewElement?: { source?: { componentKey?: string } } } | undefined)
    ?.viewElement?.source?.componentKey;
  const slotDisplayCtx = useSlotDisplayContext(componentKey);

  const groupGapCss = gapLevelToCssValue(viewElement.groupGapLevel);
  const afterNameGapCss = gapLevelToCssValue(viewElement.afterNameGap);

  return (
    <ContainerLevelContext.Provider value={level}>
      <DisplayContextProvider value={slotDisplayCtx}>
        <div
          className="cp-component-items-container"
          data-element-id={viewElement.id}
          data-container-type="component-items"
          data-container-level={level}
        >
          {/* Component + Attributes Content - always visible */}
          <div
            className="cp-component-items-container__content"
            style={groupGapCss ? ({ '--container-gap': groupGapCss } as React.CSSProperties) : undefined}
          >
            {/* Render Component */}
            {componentChild && (
              <div
                className="cp-component-items-container__component-section"
                style={afterNameGapCss ? ({ '--after-name-gap': afterNameGapCss } as React.CSSProperties) : undefined}
              >
                <ElementRenderer state={componentChild} />
              </div>
            )}

            {/* Render Attributes */}
            {attributeChildren.length > 0 && (
              <div
                className="cp-component-items-container__attributes-section"
                style={groupGapCss ? ({ '--container-gap': groupGapCss } as React.CSSProperties) : undefined}
              >
                {attributeChildren.map((attrChild) => (
                  <div
                    key={viewElementId(attrChild) || attrChild.id}
                    className="cp-component-items-container__attribute-item"
                  >
                    <ElementRenderer state={attrChild} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DisplayContextProvider>
    </ContainerLevelContext.Provider>
  );
};
